import { IJob, JobFilters } from 'src/services/jobs/jobs.types';

import { TenantType } from 'src/types/enums';

import { rootApi } from '../rootApi';
import { RestrictedAccountProfile } from '../account/account.types';
import { APIResponse, PageOptions, PaginatedResponse } from '../api.types';
import {
  PurgePreview,
  ActivationTerms,
  ActivationRequest,
  AdminOrganization,
  CreateTenantPayload,
  ActivateTenantPayload,
  RestrictedOrganizationSettings,
} from './admin.types';

const adminService = rootApi.injectEndpoints({
  endpoints: (build) => ({
    // organizations
    getAllOrganizations: build.query<PaginatedResponse<AdminOrganization>, PageOptions & { search_query?: string, tenant_type?: TenantType }>({
      query: (params) => ({
        url: '/system/organizations',
        params,
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'GlobalOrganizations' },
      ]
    }),

    getOrganization: build.query<AdminOrganization, { id: string }>({
      query: (params) => ({
        url: `/system/organizations/${params.id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'GlobalOrganizations', id: arg.id },
      ]
    }),
    updateRestrictedOrganizationSettings: build.mutation<APIResponse, { id: string, data: RestrictedOrganizationSettings }>({
      query: ({ id, data }) => ({
        url: `/system/organizations/${id}/settings`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'GlobalOrganizations', id: arg.id },
      ]
    }),

    // tenant activation (gated agency/employer onboarding)
    createTenant: build.mutation<ActivationRequest, CreateTenantPayload>({
      query: (data) => ({
        url: '/system/organizations',
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'GlobalOrganizations' }],
    }),

    getActivationRequest: build.query<ActivationRequest, { id: string }>({
      query: ({ id }) => ({ url: `/system/organizations/${id}/activation` }),
      providesTags: (_result, _error, arg) => [{ type: 'ActivationRequest', id: arg.id }],
    }),

    sendActivationLink: build.mutation<ActivationRequest, { id: string, data: ActivationTerms }>({
      query: ({ id, data }) => ({
        url: `/system/organizations/${id}/activation/send-link`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ActivationRequest', id: arg.id },
        { type: 'GlobalOrganizations', id: arg.id },
      ],
    }),

    activateTenantWithoutPayment: build.mutation<ActivationRequest, { id: string, data: ActivateTenantPayload }>({
      query: ({ id, data }) => ({
        url: `/system/organizations/${id}/activation/activate`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ActivationRequest', id: arg.id },
        { type: 'GlobalOrganizations', id: arg.id },
      ],
    }),

    declineTenant: build.mutation<ActivationRequest, { id: string }>({
      query: ({ id }) => ({
        url: `/system/organizations/${id}/activation/decline`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ActivationRequest', id: arg.id },
        { type: 'GlobalOrganizations', id: arg.id },
      ],
    }),

    // tenant purge ("nuke") - permanent removal of an incorrect or fraudulent tenant
    getPurgePreview: build.query<PurgePreview, { id: string }>({
      query: ({ id }) => ({ url: `/system/organizations/${id}/purge/preview` }),
    }),

    purgeTenant: build.mutation<PurgePreview, { id: string, confirmation: string }>({
      query: ({ id, confirmation }) => ({
        url: `/system/organizations/${id}/purge`,
        method: 'POST',
        data: { confirmation },
      }),
      invalidatesTags: [{ type: 'GlobalOrganizations' }],
    }),

    // accounts
    getAllAccounts: build.query<PaginatedResponse<RestrictedAccountProfile>, PageOptions & { search_query?: string, online_only?: boolean }>({
      query: (params) => ({
        url: '/system/accounts',
        params,
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'GlobalAccounts' },
      ]
    }),

    getAccountsByOrganization: build.query<PaginatedResponse<RestrictedAccountProfile>, { params: PageOptions; orgId: string }>({
      query: ({ params, orgId }) => ({
        url: `/system/organizations/${orgId}/accounts`,
        params,
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'GlobalAccounts', id: arg.orgId },
      ]
    }),

    // jobs
    getAllJobs: build.query<PaginatedResponse<IJob>, PageOptions & { search_query?: string }>({
      query: (params) => ({
        url: '/system/jobs',
        params,
      })
    }),

    getJobsByOrganization: build.query<PaginatedResponse<IJob>, { params: JobFilters & PageOptions; orgId: string }>({
      query: ({ params, orgId }) => ({
        url: `/system/organizations/${orgId}/jobs`,
        params,
      })
    }),

  })
});

export const {
  useGetAllOrganizationsQuery,
  useGetOrganizationQuery,
  useUpdateRestrictedOrganizationSettingsMutation,

  useCreateTenantMutation,
  useGetActivationRequestQuery,
  useSendActivationLinkMutation,
  useActivateTenantWithoutPaymentMutation,
  useDeclineTenantMutation,

  useLazyGetPurgePreviewQuery,
  usePurgeTenantMutation,

  useGetAllAccountsQuery,
  useGetAccountsByOrganizationQuery,

  useGetAllJobsQuery,
  useGetJobsByOrganizationQuery,
} = adminService;
